(() => {
  if (customElements.get('product-selector')) {
    return;
  }

  class ProductSelector extends HTMLElement {
    constructor() {
      super();

      this.addEventListener(
        'change',
        debounce(this.onOptionChange.bind(this), 200)
      );
    }

    connectedCallback() {
      this.initSlider();
      this.filterDependentOptions();

      // Initialize the currently selected variant on page load
      this.initializeCurrentVariant();
    }

    initializeCurrentVariant() {
      // Build the current options from the already selected inputs
      this.options = Array.from(
        this.querySelectorAll('select, input:checked'),
        (element) => element.value
      );

      // Find the currently selected variant
      this.updateMasterId();

      if (!this.currentVariant) {
        return;
      }

      // Wait until product-media custom element is available
      const updateGallery = () => {
        const productMedia = document.querySelector('product-media');

        if (!productMedia) {
          return false;
        }

        if (typeof productMedia.updateVariantMedia === 'function') {
          this.updateMedia();
          return true;
        }

        return false;
      };

      // Try immediately
      if (updateGallery()) {
        return;
      }

      // product-media may be connected slightly later
      let attempts = 0;

      const retry = setInterval(() => {
        attempts++;

        if (updateGallery() || attempts >= 20) {
          clearInterval(retry);
        }
      }, 100);
    }

    onOptionChange(event) {
      this.updateOptions(event);
      this.updateMasterId();

      if (
        event.target.hasAttribute('data-purchase-option') ||
        event.target.hasAttribute('data-selling-plan-option')
      ) {
        if (event.target.hasAttribute('data-selling-plan-option')) {
          this.selectSellingPlanGroup(event.target);
          this.updateSellingPlanId(event.target.value);
          this.updateProductInfo();
        } else if (event.target.hasAttribute('data-purchase-option')) {
          this.selectFirstSellingPlanGroupOption(event.target);
        }

        this.toggleAddButton(true, '');
        return;
      }

      this.filterDependentOptions();

      this.toggleAddButton(true, '');
      this.updatePickupAvailability();
      this.selectSellingPlanGroup();

      if (!this.currentVariant) {
        this.toggleAddButton(true, window.theme.strings.product.unavailable);
        this.setUnavailable();
        return;
      }

      this.updateMedia();
      if (!this.closest('modal-dialog')) this.updateURL();
      this.updateVariantInput();
      this.updateSellingPlanId('');
      this.updateProductInfo();
    }

    /**
     * filterDependentOptions
     *
     * Shows only pack options valid for the currently selected size,
     * then updates pack card prices/discount to match the selected size.
     */
    filterDependentOptions() {
      const variantData = this.getVariantData();

      const optionGroups = Array.from(
        this.querySelectorAll('.js.main-product__form-input[role="group"]')
      );

      if (optionGroups.length < 2) return;

      const selectedValues = optionGroups.map((group) => {
        const checked = group.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });

      optionGroups.forEach((group, groupIndex) => {
        if (groupIndex === 0) return;

        const validValues = new Set();

        variantData.forEach((variant) => {
          const earlierOptionsMatch = selectedValues
            .slice(0, groupIndex)
            .every((val, idx) => {
              if (val === null) return true;
              return variant.options[idx] === val;
            });

          if (earlierOptionsMatch) {
            validValues.add(variant.options[groupIndex]);
          }
        });

        const optionItems = group.querySelectorAll('.main-product__form-option');
        let currentCheckedIsVisible = false;
        let firstVisibleInput = null;

        optionItems.forEach((item) => {
          const input = item.querySelector('input[type="radio"]');
          if (!input) return;

          const isValid = validValues.has(input.value);
          item.style.display = isValid ? '' : 'none';

          if (isValid) {
            if (!firstVisibleInput) firstVisibleInput = input;
            if (input.checked) currentCheckedIsVisible = true;
          }
        });

        if (!currentCheckedIsVisible && firstVisibleInput) {
          firstVisibleInput.checked = true;
          selectedValues[groupIndex] = firstVisibleInput.value;
          firstVisibleInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      // After filtering, update pack card prices for the now-selected size
      this.updatePackCardPrices(selectedValues, optionGroups);
    }

    /**
     * updatePackCardPrices
     *
     * Reads the data-pack-prices JSON attribute embedded on each pack label
     * by Liquid, finds the entry matching the currently selected Size, and
     * re-renders the price, strikethrough, discount badge, and size meta
     * inside each visible pack card — all without a page fetch.
     *
     * Money formatting mirrors Shopify's money_without_trailing_zeros filter:
     * divides cents by 100, uses the shop currency symbol from the page, and
     * drops the decimal when the amount is a whole number.
     */
    updatePackCardPrices(selectedValues, optionGroups) {
      // Find which group index is "Size" and which is "Pack"
      let sizeGroupIndex = -1;
      optionGroups.forEach((group, i) => {
        const name = group.dataset.optionName || '';
        if (name === 'Size') sizeGroupIndex = i;
      });

      // Fallback: size is always index 0 if not found via data attribute
      const currentSize =
        sizeGroupIndex >= 0
          ? selectedValues[sizeGroupIndex]
          : selectedValues[0];

      if (!currentSize) return;

      // Update every visible pack card label
      this.querySelectorAll('.variant-pack-label[data-pack-prices]').forEach((label) => {
        let priceMap;
        try {
          priceMap = JSON.parse(label.dataset.packPrices);
        } catch (e) {
          return;
        }

        const data = priceMap[currentSize];
        if (!data) return;

        const price           = data.price;           // in cents
        const compareAtPrice  = data.compare_at_price; // in cents (0 or null if none)

        // Calculate discount %
        let discountPct = 0;
        if (compareAtPrice && compareAtPrice > price) {
          discountPct = Math.round((compareAtPrice - price) * 100 / compareAtPrice);
        }

        // Format money (cents → currency string)
        const fmt = (cents) => {
          const symbol = window.Shopify && window.Shopify.currency
            ? (window.theme && window.theme.moneyFormat
                ? ''   // use theme format below
                : window.Shopify.currency.active === 'INR' ? '₹' : '$')
            : '₹';

          const amount = cents / 100;
          return symbol + (Number.isInteger(amount) ? amount.toLocaleString('en-IN') : amount.toFixed(2));
        };

        // Rebuild inner HTML
        const inner = label.querySelector('.variant-pack-inner');
        if (!inner) return;

        const packName = label.dataset.packValue || label.querySelector('.variant-pack-name')?.textContent || '';

        inner.innerHTML = `
          ${discountPct > 0 ? `<div class="variant-pack-badge"><span class="ds-bg-cs">${discountPct}% Off</span></div>` : ''}
          <div class="variant-pack-prices">
            ${compareAtPrice && compareAtPrice > price
              ? `<span class="variant-pack-compare">${fmt(compareAtPrice)}</span>`
              : ''}
            <span class="variant-pack-price">${fmt(price)}</span>
          </div>
          <div class="variant-pack-meta">${currentSize}</div>
          <div class="variant-pack-name">${packName}</div>
        `;
      });
    }

    initSlider() {
      const sliderList = this.querySelectorAll('.splide');

      if (!sliderList.length) return;

      const mainSettings = {
        trimSpace: 'move',
        autoWidth: true,
        waitForTransition: true,
        pagination: false,
        arrows: true,
        focus: 0,
        omitEnd: true,
        speed: 300,
        easing: 'ease-in-out',
        gap: '0.8rem',
      };

      sliderList.forEach(function (sliderListEl) {
        const slider = new Splide(sliderListEl, mainSettings);

        slider.on('ready resize', () => {
          setTimeout(() => {
            const TOLERANCE = 4;

            const childrenWidth = [...slider.Components.Elements.slides].reduce(
              (acc, val) => {
                const style = val.currentStyle || window.getComputedStyle(val);
                acc += val.clientWidth + parseInt(style.marginRight);
                return acc;
              },
              0
            );

            const wrapperWidth =
              slider.Components.Elements.list.clientWidth + TOLERANCE;

            if (childrenWidth <= wrapperWidth) {
              slider.options = { arrows: false, drag: false };
            } else {
              slider.options = { arrows: true, drag: true };
              sliderListEl.classList.add('is-overflow');
              sliderListEl.parentElement.classList.add('is-slider-active');
            }
          }, 200);
        });

        slider.mount();
      });
    }

    updateOptions(event) {
      const { name, value } = event.target;
      const targetInstance = name.includes('options[')
        ? this.querySelector(
            `[name="${name.split('options[')[1].split(']')[0]}"][value="${value}"]`
          )
        : this.querySelector(`[name="options[${name}]"]`);

      if (targetInstance) {
        if (targetInstance.type === 'select-one') {
          targetInstance.value = value;
        } else if (targetInstance.type === 'radio') {
          targetInstance.checked = true;
        }
      }

      this.options = Array.from(
        this.querySelectorAll('select, input:checked'),
        (element) => {
          const valueLabel = this.querySelector(`#${element.dataset.valueLabel}`);
          const elementValue = element.value;
          if (!valueLabel) return elementValue;
          const valueLabelString = valueLabel.hasAttribute('data-option-value')
            ? `${elementValue}`
            : `: ${elementValue}`;
          valueLabel.innerText = valueLabelString;
          return elementValue;
        }
      );
    }

    selectSellingPlanGroup(checkedInput) {
      const input =
        checkedInput || this.querySelector('#Product-Purchase-Option-One-Time');

      if (!input) {
        this.querySelectorAll(
          '[name="purchase_option"], [data-selling-plan-option]'
        ).forEach((option) => {
          option.checked = false;
        });
        return;
      }

      if (checkedInput) {
        checkedInput
          .closest('[data-purchase-option-wrapper]')
          .querySelector('[data-purchase-option]').checked = true;
      } else {
        const sellingPlanGroupIds = this.currentVariant.selling_plan_allocations
          .map((alloc) => alloc.selling_plan_group_id)
          .join(',');

        input.checked = true;
        this.querySelectorAll('[data-purchase-option-wrapper]').forEach(
          (plan) => {
            if (!plan.hasAttribute('data-selling-plan-group-id')) return;
            plan.classList.toggle(
              'hidden',
              !sellingPlanGroupIds.includes(plan.dataset.sellingPlanGroupId)
            );
          }
        );
      }

      this.updateURL(input.value);
      this.querySelectorAll('[data-selling-plan-option]').forEach((option) => {
        if (option === checkedInput) return;
        option.checked = false;
      });
    }

    selectFirstSellingPlanGroupOption(checkedInput) {
      this.querySelectorAll('[data-selling-plan-option]').forEach((option) => {
        option.checked = false;
      });

      const firstSellingPlanOption = checkedInput
        .closest('[data-purchase-option-wrapper]')
        .querySelector('[data-selling-plan-option]');

      if (!firstSellingPlanOption) {
        this.updateURL();
        this.updateSellingPlanId('');
        this.updateProductInfo();
        return;
      }

      firstSellingPlanOption.checked = true;
      this.updateURL(firstSellingPlanOption.value);
      this.updateSellingPlanId(firstSellingPlanOption.value);
      this.updateProductInfo();
    }

    updateSellingPlanId(sellingPlanId) {
      const sellingPlanInput = document.querySelector(
        'product-form [name="selling_plan"]'
      );
      sellingPlanInput.value = sellingPlanId;
    }

    updateMasterId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options
          .map((option, index) => {
            return this.options[index] === option;
          })
          .includes(false);
      });
    }

    // updateMedia() {
    //   if (!this.currentVariant.featured_media) return;

    //   const productMedia =
    //     document.querySelector(
    //       `[data-product-quickview-media][data-media-id="${this.currentVariant.featured_media.id}"]`
    //     ) || document.querySelector('product-media');

    //   if (!productMedia) return;

    //   if (typeof productMedia.goToSlide === 'function') {
    //     productMedia.goToSlide(this.currentVariant.featured_media.id);
    //   } else {
    //     document
    //       .querySelectorAll('[data-product-quickview-media]')
    //       .forEach((media) => {
    //         media.style.display = 'none';
    //       });
    //     productMedia.style.display = 'block';
    //   }
    // }


    updateMedia() {
      if (!this.currentVariant) return;

      const productMedia = document.querySelector('product-media');

      if (!productMedia) return;

      if (typeof productMedia.updateVariantMedia === 'function') {
        productMedia.updateVariantMedia(
          this.currentVariant.id,
          this.currentVariant.featured_media
            ? this.currentVariant.featured_media.id
            : null
        );
      }
    }

    updateURL(sellingPlanId) {
      if (!window.location.href.includes('/products')) return;

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('variant', this.currentVariant.id);

      if (sellingPlanId) {
        searchParams.set('selling_plan', sellingPlanId);
      } else {
        searchParams.delete('selling_plan');
      }

      window.history.replaceState(
        {},
        '',
        `${this.dataset.url}?${searchParams.toString()}`
      );
    }

    updateVariantInput() {
      const productForms = document.querySelectorAll(
        `#product-form-${this.dataset.section}, #product-form-installment`
      );

      if (this.hasAttribute('data-variant-id')) {
        this.setAttribute('data-variant-id', this.currentVariant.id);
      }

      productForms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updatePickupAvailability() {
      const pickUpAvailabilityNode = document.querySelector('pickup-availability');

      if (!pickUpAvailabilityNode || !this.currentVariant) return;

      pickUpAvailabilityNode.dataset.variantId = this.currentVariant.id;

      if (this.currentVariant && this.currentVariant.available) {
        pickUpAvailabilityNode.setAttribute('available', '');
        pickUpAvailabilityNode.fetchAvailability(
          pickUpAvailabilityNode.dataset.variantId
        );
        return;
      }

      pickUpAvailabilityNode.removeAttribute('available');
      pickUpAvailabilityNode.innerHTML = '';
    }

    updateProductInfo() {
      const sellingPlansWrapper = this.querySelector('[data-selling-plans-wrapper]');
      if (sellingPlansWrapper) sellingPlansWrapper.classList.add('is-loading');

      const variantId = this.currentVariant ? this.currentVariant.id : null;

      const fetchUrl = variantId
        ? `${this.dataset.url}?variant=${variantId}&section_id=${this.dataset.section}`
        : `${this.dataset.url}?${new URLSearchParams(window.location.search).toString()}&section_id=${this.dataset.section}`;

      fetch(fetchUrl)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          this.changeProductPrice(html);
          this.updateInventoryNotice(html);

          this.hasAttribute('data-product-requires-selling-plan') &&
          !document.querySelector('[name="selling_plan"]').value
            ? this.toggleAddButton(true, 'Please select selling plan')
            : this.toggleAddButton(
                !this.currentVariant.available,
                window.theme.strings.product.soldOut
              );

          if (sellingPlansWrapper) {
            this.changeSellingPlansPrices(html);
            sellingPlansWrapper.classList.remove('is-loading');
          }

          this.changeSellingPlanDescription(html);
        });
    }

    updateInventoryNotice(html) {
      const destination = document.querySelector('[data-inventory-notice]');
      const source = html.querySelector('[data-inventory-notice]');
      if (source && destination) destination.innerHTML = source.innerHTML;
    }

    changeProductPrice(html) {
      const id = `price-${this.dataset.section}`;
      const destination = document.getElementById(id);
      const source = html.getElementById(id);

      if (!source || !destination) return;

      const existingOffersBtn = destination.querySelector('[id^="offersOpen-"]');
      const savedOffersWrapper = existingOffersBtn
        ? existingOffersBtn.closest('.active-offers')
          ? existingOffersBtn.closest('.active-offers').cloneNode(true)
          : existingOffersBtn.parentElement.cloneNode(true)
        : null;

      destination.innerHTML = source.innerHTML;
      destination.className = source.className;
      destination.classList.remove('visibility-hidden');

      if (savedOffersWrapper && !destination.querySelector('[id^="offersOpen-"]')) {
        const priceInfo = destination.querySelector('.price-info');
        if (priceInfo) {
          priceInfo.appendChild(savedOffersWrapper);
        }
      }

      this.reinitBottomDrawer();
    }

    reinitBottomDrawer() {
      const openBtn = document.querySelector('[id^="offersOpen-"]');
      if (!openBtn) return;

      const sid = openBtn.id.replace('offersOpen-', '');
      const closeBtn = document.getElementById('offersClose-' + sid);
      const overlay  = document.getElementById('offersOverlay-' + sid);
      const sheet    = document.getElementById('offersSheet-'   + sid);

      if (!sheet) return;

      function openSheet() {
        sheet.classList.add('active');
        overlay.classList.add('active');
        document.body.style.overflow = 'hidden';
        document.body.classList.add('open-offer-popup');
      }

      function closeSheet() {
        sheet.classList.remove('active');
        overlay.classList.remove('active');
        document.body.style.overflow = '';
        document.body.classList.remove('open-offer-popup');
      }

      const newOpenBtn = openBtn.cloneNode(true);
      openBtn.parentNode.replaceChild(newOpenBtn, openBtn);
      newOpenBtn.addEventListener('click', openSheet);

      if (closeBtn) {
        const newCloseBtn = closeBtn.cloneNode(true);
        closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn);
        newCloseBtn.addEventListener('click', closeSheet);
      }

      if (overlay) {
        const newOverlay = overlay.cloneNode(true);
        overlay.parentNode.replaceChild(newOverlay, overlay);
        newOverlay.addEventListener('click', closeSheet);
      }

      sheet.querySelectorAll('.offer-copy').forEach(function (btn) {
        const newBtn = btn.cloneNode(true);
        btn.parentNode.replaceChild(newBtn, btn);
        newBtn.addEventListener('click', function () {
          const code = newBtn.getAttribute('data-code');
          if (navigator.clipboard) navigator.clipboard.writeText(code).catch(function () {});
          newBtn.classList.add('copied');
          newBtn.innerHTML =
            '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
          setTimeout(function () {
            newBtn.classList.remove('copied');
            newBtn.innerHTML =
              '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
          }, 1800);
        });
      });

      sheet.querySelectorAll('.offer-readmore').forEach(function (btn) {
        const newBtn = btn.cloneNode(true);
        btn.parentNode.replaceChild(newBtn, btn);
        newBtn.addEventListener('click', function () {
          const el = document.getElementById(newBtn.getAttribute('data-extra'));
          const isOpen = el.style.display !== 'none';
          el.style.display = isOpen ? 'none' : 'block';
          newBtn.textContent = isOpen ? 'Read more' : 'Read less';
        });
      });
    }

    changeSellingPlansPrices(html) {
      const onTimePriceId = `#price-one-time-${this.dataset.section}`;
      const plansPricesDestinations = document.querySelectorAll(
        '[data-selling-plan-group-price]'
      );

      if (!this.querySelector(onTimePriceId) && !plansPricesDestinations) return;

      if (this.querySelector(onTimePriceId)) {
        this.querySelector(onTimePriceId).textContent =
          html.querySelector(onTimePriceId).textContent;
      }

      if (plansPricesDestinations) {
        plansPricesDestinations.forEach((destination) => {
          destination.innerHTML = html.querySelector(
            `[data-selling-plan-group-price][data-selling-plan-group-name="${destination.dataset.sellingPlanGroupName}"]`
          ).innerHTML;
        });
      }
    }

    changeSellingPlanDescription(html) {
      const sellingPlansDescription = this.querySelector(
        '[data-selling-plan-description]'
      );

      if (sellingPlansDescription) {
        sellingPlansDescription.innerHTML = html.querySelector(
          '[data-selling-plan-description]'
        ).innerHTML;
      }
    }

    toggleAddButton(disable = true, text) {
      const productForm = document.getElementById(
        `product-form-${this.dataset.section}`
      );

      if (!productForm) return;

      const addButton = productForm.querySelector('[name="add"]');
      const buyNowButton = productForm.querySelector('[data-fast-checkout]');
      const addButtonText = productForm.querySelector('[name="add"] > span');

      if (!addButton) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        buyNowButton && buyNowButton.setAttribute('disabled', 'disabled');

        if (text) {
          addButtonText.textContent = text;
          addButton.classList.remove('is-loading');
        } else {
          addButton.classList.add('is-loading');
        }
      } else {
        addButton.removeAttribute('disabled');
        buyNowButton && buyNowButton.removeAttribute('disabled');
        addButtonText.textContent = window.theme.strings.product.addToCart;
        addButton.classList.remove('is-loading');
        buyNowButton && buyNowButton.classList.remove('is-loading');
      }
    }

    setUnavailable() {
      const button = document.getElementById(
        `product-form-${this.dataset.section}`
      );
      const addButton = button.querySelector('[name="add"]');
      const addButtonText = button.querySelector('[name="add"] > span');
      const price = document.getElementById(`price-${this.dataset.section}`);

      if (!addButton) return;

      addButtonText.textContent = window.theme.strings.product.unavailable;
      if (price) price.classList.add('visibility-hidden');
    }

    getVariantData() {
      return JSON.parse(this.querySelector('[data-product-json]').textContent)
        .variants;
    }
  }

  customElements.define('product-selector', ProductSelector);
})();
